import { type CSSProperties } from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { type GroupedAssetRow } from '../utils/cartTransforms';

// Cell components shared by AssetCartTable (desktop) and AssetCartCard
// (below lg) so both render one source of truth per line.

// Up to this many thumbnails fan out; the rest collapse into a "+N" badge.
const FAN_MAX = 3;
// The stack's horizontal offset lives in the `--fan-offset` CSS variable,
// declared via Tailwind classes on the container: 14px at rest (a tight
// stack), 108px on hover so each 100px-wide thumbnail spreads fully clear of
// the next. Tailwind needs those values as literals, so they're inline in the
// className rather than constants here. The container's box stays at its
// resting width — on hover the thumbnails overflow it (and the column) and a
// constant z-index keeps them painted over the neighboring content.

export const AssetServiceCell = ({
  item,
  spreadOnHover = true,
}: {
  item: GroupedAssetRow;
  /**
   * The card layout passes false: hover never fires on touch, and on a
   * narrow pointer viewport the spread would escape the card's box. The
   * photo count stays reachable via the "+N" badge and the edit modal.
   */
  spreadOnHover?: boolean;
}) => {
  // Thumbnail urls follow assetRefs; the singular assetUrl is the fallback for
  // confirmation rows that carry no refs.
  const urls = (
    item.assetRefs?.length
      ? item.assetRefs.map((r) => r.assetUrl ?? '')
      : [item.assetUrl]
  ).filter(Boolean);
  const shown = urls.slice(0, FAN_MAX);
  const overflow = urls.length - shown.length;
  const serviceName = item.service?.name || 'Image Edit';

  return (
    <div className="flex items-center gap-3">
      {shown.length <= 1 ? (
        <img
          src={shown[0] ?? item.assetUrl}
          height={66}
          width={100}
          alt=""
          className="shrink-0 rounded-md bg-muted object-cover"
        />
      ) : (
        <div
          // The resting offset MUST come from a class, not inline style: an
          // inline `--fan-offset` declaration outranks the `hover:` rule
          // (inline beats any selector), so the spread would never trigger.
          // The width is a fixed literal (resting footprint, NOT var-driven) so
          // the box never grows and the row doesn't reflow; the thumbnails
          // overflow it on hover. z-10 is permanent, not hover-gated: on hover
          // the thumbnails spread past the cell, and a hover-only z-index would
          // drop the moment the pointer leaves — flashing them behind the next
          // column mid-collapse. Constant z-10 keeps them on top throughout and
          // is invisible at rest (the resting stack never overflows the cell).
          className={cn(
            'relative z-10 h-[66px] shrink-0 [--fan-offset:14px]',
            spreadOnHover && 'hover:[--fan-offset:108px]',
          )}
          style={
            {
              width: `calc(100px + ${shown.length - 1} * 14px)`
            } as CSSProperties
          }
          aria-label={`${urls.length} photos in this order`}
        >
          {shown.map((url, i) => {
            const isBackMost = i === shown.length - 1;
            return (
              <div
                key={url + i}
                className="absolute top-0 h-[66px] w-[100px] transition-[left] duration-300 ease-out motion-reduce:transition-none"
                style={{
                  left: `calc(${i} * var(--fan-offset))`,
                  zIndex: shown.length - i
                }}
              >
                <img
                  src={url}
                  alt=""
                  className="h-full w-full rounded-md bg-muted object-cover ring-2 ring-white"
                />
                {overflow > 0 && isBackMost && (
                  <span className="absolute inset-0 grid place-items-center rounded-md bg-black/30 text-sm font-semibold text-white ring-2 ring-white">
                    +{overflow}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Typography variant="body-medium">{serviceName}</Typography>
    </div>
  );
};

export const DescriptionCell = ({
  row,
  onEdit,
  readOnly = false
}: {
  row: GroupedAssetRow;
  onEdit?: (row: GroupedAssetRow) => void;
  readOnly?: boolean;
}) => {
  const label = `Edit description for ${row.service?.name || 'image edit'}`;
  return (
    <div className="flex items-center gap-2">
      <Typography variant="body" className="flex-1 break-words">
        {row.additionalNotes || '-'}
      </Typography>
      {!readOnly && onEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-primary"
          aria-label={label}
          onClick={() => onEdit(row)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
